import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import SEOMeta from '@/components/feature/SEOMeta';
import StructuredData from '@/components/feature/StructuredData';
import { categories, getHotMovies, getNewMovies, getFeaturedMovies, type Movie, type CategoryType } from '@/mocks/movies';

export default function HomePage() {
  const [activeCategory, setActiveCategory] = useState<CategoryType>('movie');
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();

  const hotMovies = getHotMovies();
  const newMovies = getNewMovies();
  const featuredMovies = getFeaturedMovies();

  const categoryMovies = hotMovies.filter(m => m.category === activeCategory).slice(0, 8);

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (searchValue.trim()) {
      navigate(`/category/movie?search=${encodeURIComponent(searchValue.trim())}`);
    }
  }, [searchValue, navigate]);

  const siteUrl = 'https://777cinema.com';

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '777影院',
      url: siteUrl,
      description: '777影院提供高清免费影视资源，电影、电视剧、综艺全集在线观看，超清画质，流畅不卡顿，免费无广告，极速播放。',
      logo: `${siteUrl}/logo.png`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '777影院',
      url: siteUrl,
      description: '777影院提供高清免费影视资源，电影、电视剧、综艺全集在线观看，超清画质，流畅不卡顿，免费无广告，极速播放。',
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/category/movie?search={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '777影院 - 首页',
      url: siteUrl,
      description: '777影院首页，最新最热影视推荐，电影电视剧综艺动漫在线观看',
      isPartOf: {
        '@type': 'WebSite',
        name: '777影院',
        url: siteUrl,
      },
    },
  ];

  return (
    <>
      <SEOMeta
        title="777影院 - 777影院高清免费 | 电影电视剧全集在线观看超清不卡顿"
        description="777影院提供高清免费影视资源，电影、电视剧、综艺全集在线观看，超清画质，流畅不卡顿，免费无广告，极速播放。"
        keywords="777影院高清,777影院免费,777影院超清,在线观看,电影大全,电视剧全集,不卡,流畅,极速,无广告,免费播放"
        canonicalUrl={siteUrl}
        ogType="website"
      />
      <StructuredData data={structuredData} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero */}
        <section className="relative h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Artistic%20abstract%20cinema%20themed%20background%20with%20warm%20red%20and%20gold%20flowing%20curtains%20texture%2C%20soft%20dramatic%20lighting%20with%20bokeh%20effects%2C%20elegant%20theater%20atmosphere%2C%20rich%20dark%20crimson%20and%20amber%20gradient%2C%20minimalist%20abstract%20composition%2C%20luxurious%20and%20cinematic%20feel%2C%20no%20text%2C%20pure%20visual%20art&width=1800&height=1000&seq=hero-bg-home-01&orientation=landscape"
            alt="777影院背景"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
              高清免费影视<span className="text-accent-400">，畅享无限</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              电影、电视剧、综艺、动漫，超清画质，流畅不卡顿，完全免费，无广告打扰。777影院，你的私人影院。
            </p>
            <form onSubmit={handleSearch} className="max-w-xl mx-auto relative">
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="搜索你喜欢的电影、电视剧、综艺..."
                className="w-full pl-12 pr-28 py-3.5 md:py-4 rounded-full text-sm md:text-base bg-white/95 border-2 border-transparent focus:border-primary-400 text-foreground-900 placeholder:text-foreground-400 outline-none transition-all duration-300 shadow-lg"
              />
              <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg"></i>
              <button
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-primary-500 hover:bg-primary-600 text-white font-medium px-5 py-2 rounded-full text-sm transition-colors duration-200 whitespace-nowrap"
              >
                搜索
              </button>
            </form>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-center gap-2 mb-10 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => setActiveCategory(cat.key)}
                  className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeCategory === cat.key
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {categoryMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>

            <div className="text-center mt-8">
              <Link
                to={`/category/${activeCategory}`}
                className="inline-flex items-center gap-1.5 text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors duration-200 whitespace-nowrap"
              >
                查看全部{activeCategory === 'movie' ? '电影' : activeCategory === 'tv' ? '电视剧' : activeCategory === 'variety' ? '综艺' : '动漫'}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Featured Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  <i className="ri-fire-line text-accent-500 mr-2"></i>
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">编辑精选，品质保证</p>
              </div>
              <Link to="/category/movie" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap">
                更多 <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {featuredMovies.slice(0, 5).map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  <i className="ri-timer-flash-line text-primary-500 mr-2"></i>
                  最新上架
                </h2>
                <p className="text-sm text-foreground-500 mt-1">每日更新，追剧必备</p>
              </div>
              <Link to="/category/tv" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap">
                更多 <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {newMovies.slice(0, 6).map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-10">
              为什么选择 <span className="text-primary-500">777影院</span>？
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-4k-line', title: '超清画质', desc: '支持4K超清播放，画面细腻，色彩饱满，给你影院级观影体验' },
                { icon: 'ri-speed-up-line', title: '极速流畅', desc: '全球CDN加速，秒加载不卡顿，随时随地流畅观影' },
                { icon: 'ri-forbid-2-line', title: '无广告', desc: '纯净观影体验，全程无广告打扰，沉浸式享受每一部作品' },
                { icon: 'ri-coin-line', title: '完全免费', desc: '所有影视资源完全免费，无需付费，无需会员，想看就看' },
              ].map((item) => (
                <div key={item.title} className="text-center p-6">
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 flex items-center justify-center bg-primary-50 rounded-2xl">
                    <i className={`${item.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-950 mb-2 text-sm md:text-base">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-14 md:py-20 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cinema%20interior%20with%20empty%20red%20theater%20seats%2C%20soft%20dramatic%20spotlight%20from%20above%2C%20elegant%20and%20cozy%20atmosphere%2C%20rich%20burgundy%20and%20gold%20color%20tones%2C%20cinematic%20photography%20style%2C%20peaceful%20and%20inviting%20mood%2C%20no%20people&width=1800&height=600&seq=cta-bg-home-02&orientation=landscape"
            alt="777影院观影体验"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
              立即开始你的观影之旅
            </h2>
            <p className="text-white/70 text-sm md:text-base mb-8 max-w-lg mx-auto">
              海量高清影视资源等你发现，随时随地，想看就看
            </p>
            <Link
              to="/category/movie"
              className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-white font-semibold px-8 py-3 rounded-full transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg"></i>
              立即观影
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}